import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const VARS = ["{{first_name}}", "{{last_name}}", "{{full_name}}", "{{uid}}"];
const smsSegments = (t) => { const n = (t || "").length; return n === 0 ? 0 : Math.ceil(n / 160); };

export default function Templates() {
  const [channel, setChannel] = useState("sms");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template obj or {} for new
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/message-templates?channel=${channel}`); setItems(data); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [channel]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/message-templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Templates</h1>
          <p className="text-muted-foreground">Reusable SMS &amp; Email templates for bulk messaging</p>
        </div>
        <button data-testid="new-template-btn" onClick={() => setEditing({ channel, name: "", subject: "", body: "" })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">
          <Icons.Plus size={18} /> New {channel === "sms" ? "SMS" : "Email"} Template
        </button>
      </div>

      {/* Channel tabs */}
      <div className="flex gap-1 border-b border-border">
        {[["sms", "SMS Templates", "MessageSquare"], ["email", "Email Templates", "Mail"]].map(([k, label, icon]) => {
          const Icon = Icons[icon];
          return (
            <button key={k} data-testid={`template-tab-${k}`} onClick={() => setChannel(k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${channel === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon size={15} /> {label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-muted-foreground py-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-md p-10 text-center" data-testid="templates-empty">
          <Icons.FileStack className="mx-auto text-muted-foreground mb-3" size={32} />
          <p className="text-muted-foreground">No {channel === "sms" ? "SMS" : "email"} templates yet. Create one to reuse in bulk messages.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="templates-list">
          {items.map((t) => (
            <div key={t.id} data-testid={`template-card-${t.id}`}
              className="bg-card border border-border rounded-md p-4 shadow-sm flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold truncate">{t.name}</h3>
                {t.can_edit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground"><Icons.Pencil size={15} /></button>
                    <button onClick={() => remove(t)} data-testid={`template-delete-${t.id}`} className="p-1.5 rounded hover:bg-muted text-destructive"><Icons.Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              {channel === "email" && t.subject && <div className="text-xs font-medium text-primary mt-1 truncate">{t.subject}</div>}
              <p className="text-sm text-muted-foreground mt-2 line-clamp-4 whitespace-pre-wrap break-words flex-1">{t.body}</p>
              <div className="text-[11px] text-muted-foreground mt-3 pt-2 border-t border-border/50">
                {channel === "sms" && <span>{(t.body || "").length} chars · {smsSegments(t.body)} SMS · </span>}
                by {t.created_by_name || "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateModal tpl={editing} channel={channel} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateModal({ tpl, channel, onClose, onSaved }) {
  const isNew = !tpl.id;
  const [name, setName] = useState(tpl.name || "");
  const [subject, setSubject] = useState(tpl.subject || "");
  const [body, setBody] = useState(tpl.body || "");
  const [busy, setBusy] = useState(false);

  const insertVar = (v) => setBody((b) => b + v);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the template a name");
    if (!body.trim()) return toast.error("Template body cannot be empty");
    if (channel === "email" && !subject.trim()) return toast.error("Email templates need a subject");
    setBusy(true);
    try {
      const payload = { name, channel, subject, body };
      if (isNew) await api.post("/message-templates", payload);
      else await api.put(`/message-templates/${tpl.id}`, payload);
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-md w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto" data-testid="template-modal">
        <h2 className="text-xl font-head font-extrabold mb-4">{isNew ? "New" : "Edit"} {channel === "sms" ? "SMS" : "Email"} Template</h2>
        <label className="label-caps">Template Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name"
          placeholder="e.g. Follow-up reminder" className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background" />
        {channel === "email" && (
          <>
            <label className="label-caps">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject"
              placeholder="e.g. Your Grian Solar update" className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background" />
          </>
        )}
        <label className="label-caps">Message</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="template-body" rows={channel === "sms" ? 4 : 8}
          placeholder="Type your message… use variables below to personalise" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background resize-y" />
        {channel === "sms" && (
          <div className="text-xs text-muted-foreground mt-1" data-testid="template-sms-count">
            {body.length} characters · {smsSegments(body)} SMS{body.length > 160 ? " (Uniden sends as multiple messages)" : ""}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className="text-xs text-muted-foreground self-center mr-1">Insert:</span>
          {VARS.map((v) => (
            <button key={v} type="button" onClick={() => insertVar(v)} data-testid={`template-var-${v.replace(/[{}]/g, "")}`}
              className="text-xs px-2 py-1 rounded bg-muted hover:bg-primary/10 hover:text-primary transition-colors font-mono">{v}</button>
          ))}
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="template-save"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-60">{busy ? "Saving…" : "Save Template"}</button>
        </div>
      </div>
    </div>
  );
}
